import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { tournamentAPI, playerAPI } from '../services/api';

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  tournaments: [],
  players: [],
  currentView: 'home', // 'home', 'admin', 'tournament', 'player'
  selectedTournament: null,
  selectedPlayer: null,
  loading: false,
  error: null,
  notifications: []
};

// Action types
export const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  SET_TOURNAMENTS: 'SET_TOURNAMENTS',
  ADD_TOURNAMENT: 'ADD_TOURNAMENT',
  UPDATE_TOURNAMENT: 'UPDATE_TOURNAMENT',
  DELETE_TOURNAMENT: 'DELETE_TOURNAMENT',
  SET_PLAYERS: 'SET_PLAYERS',
  ADD_PLAYER: 'ADD_PLAYER',
  UPDATE_PLAYER: 'UPDATE_PLAYER',
  DELETE_PLAYER: 'DELETE_PLAYER',
  SET_CURRENT_VIEW: 'SET_CURRENT_VIEW',
  SET_SELECTED_TOURNAMENT: 'SET_SELECTED_TOURNAMENT',
  SET_SELECTED_PLAYER: 'SET_SELECTED_PLAYER',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case ActionTypes.LOGIN:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        error: null
      };
    
    case ActionTypes.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        currentView: 'home'
      };
    
    case ActionTypes.SET_TOURNAMENTS:
      return { ...state, tournaments: action.payload };
    
    case ActionTypes.ADD_TOURNAMENT:
      return { ...state, tournaments: [...state.tournaments, action.payload] };
    
    case ActionTypes.UPDATE_TOURNAMENT:
      return {
        ...state,
        tournaments: state.tournaments.map(t => 
          t.id === action.payload.id ? action.payload : t
        )
      };
    
    case ActionTypes.DELETE_TOURNAMENT:
      return {
        ...state,
        tournaments: state.tournaments.filter(t => t.id !== action.payload)
      };
    
    case ActionTypes.SET_PLAYERS:
      return { ...state, players: action.payload };
    
    case ActionTypes.ADD_PLAYER:
      return { ...state, players: [...state.players, action.payload] };
    
    case ActionTypes.UPDATE_PLAYER:
      return {
        ...state,
        players: state.players.map(p => 
          p.id === action.payload.id ? action.payload : p
        )
      };
    
    case ActionTypes.DELETE_PLAYER:
      return {
        ...state,
        players: state.players.filter(p => p.id !== action.payload)
      };
    
    case ActionTypes.SET_CURRENT_VIEW:
      return { ...state, currentView: action.payload };
    
    case ActionTypes.SET_SELECTED_TOURNAMENT:
      return { ...state, selectedTournament: action.payload };
    
    case ActionTypes.SET_SELECTED_PLAYER:
      return { ...state, selectedPlayer: action.payload };
    
    case ActionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, action.payload]
      };
    
    case ActionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
    
    default:
      return state;
  }
};

// Context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Helper functions
  const showNotification = useCallback((message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    dispatch({ type: ActionTypes.ADD_NOTIFICATION, payload: notification });
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      dispatch({ type: ActionTypes.REMOVE_NOTIFICATION, payload: notification.id });
    }, 5000);
  }, [dispatch]);

  const login = (userData) => {
    dispatch({ type: ActionTypes.LOGIN, payload: userData });
    showNotification('Successfully logged in!', 'success');
  };

  const logout = () => {
    dispatch({ type: ActionTypes.LOGOUT });
    showNotification('Successfully logged out!', 'info');
  };

  const addTournament = useCallback(async (tournament) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      const response = await tournamentAPI.create(tournament);
      dispatch({ type: ActionTypes.ADD_TOURNAMENT, payload: response.data });
      showNotification('Tournament created successfully!', 'success');
    } catch (error) {
      console.error('Error creating tournament:', error);
      showNotification('Failed to create tournament. Please try again.', 'error');
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [showNotification]);

  const updateTournament = useCallback(async (tournament) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      const response = await tournamentAPI.update(tournament.id, tournament);
      dispatch({ type: ActionTypes.UPDATE_TOURNAMENT, payload: response.data });
      showNotification('Tournament updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating tournament:', error);
      showNotification('Failed to update tournament. Please try again.', 'error');
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [showNotification]);

  const deleteTournament = useCallback(async (tournamentId) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      await tournamentAPI.delete(tournamentId);
      dispatch({ type: ActionTypes.DELETE_TOURNAMENT, payload: tournamentId });
      showNotification('Tournament deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting tournament:', error);
      showNotification('Failed to delete tournament. Please try again.', 'error');
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [showNotification]);

  const addPlayer = useCallback(async (player) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      const response = await playerAPI.create(player);
      dispatch({ type: ActionTypes.ADD_PLAYER, payload: response.data });
      showNotification('Player registered successfully!', 'success');
    } catch (error) {
      console.error('Error creating player:', error);
      showNotification('Failed to register player. Please try again.', 'error');
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [showNotification]);

  const updatePlayer = useCallback(async (player) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      const response = await playerAPI.update(player.id, player);
      dispatch({ type: ActionTypes.UPDATE_PLAYER, payload: response.data });
      showNotification('Player updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating player:', error);
      showNotification('Failed to update player. Please try again.', 'error');
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [showNotification]);

  const deletePlayer = useCallback(async (playerId) => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      await playerAPI.delete(playerId);
      dispatch({ type: ActionTypes.DELETE_PLAYER, payload: playerId });
      showNotification('Player deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting player:', error);
      showNotification('Failed to delete player. Please try again.', 'error');
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [showNotification]);

  // Load tournaments from API
  const loadTournaments = useCallback(async () => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      const response = await tournamentAPI.getAll();
      dispatch({ type: ActionTypes.SET_TOURNAMENTS, payload: response.data.data || [] });
    } catch (error) {
      console.error('Error loading tournaments:', error);
      showNotification('Failed to load tournaments. Please try again.', 'error');
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [showNotification]);

  // Load players from API
  const loadPlayers = useCallback(async () => {
    try {
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      const response = await playerAPI.getAll();
      dispatch({ type: ActionTypes.SET_PLAYERS, payload: response.data.data || [] });
    } catch (error) {
      console.error('Error loading players:', error);
      showNotification('Failed to load players. Please try again.', 'error');
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [showNotification]);

  const value = {
    ...state,
    dispatch,
    ActionTypes,
    showNotification,
    login,
    logout,
    loadTournaments,
    loadPlayers,
    addTournament,
    updateTournament,
    deleteTournament,
    addPlayer,
    updatePlayer,
    deletePlayer
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
